import { useEffect, useMemo, useState } from "react"
import { useLocation } from "react-router-dom"

import styles from "./styles.module.scss"

import InfiniteCardList from "../../../components/InfiniteCardList"
import AnimeFilters from "../../../components/AnimeFilters"
import ResultNotFound from "../../../components/ResultNotFound"
import Loading from "../../../components/Loading"
import LoadingCardSkeleton from "../../../components/LoadingCardSkeleton"
import GridContainer from "../../../components/ui/GridContainer"
import { CARD_TYPES } from "../../../utils/constants"
import {
  getCategoryLabel,
  getCollectionsByCategory,
  resolveCategoryKey
} from "../../../utils/mai2Collections"

const CATEGORY_LAYOUT = {
  chara: {
    gridSize: { xs: 6, xsm: 4, sm: 3, md: 2, lg: 2 }
  },
  icon: {
    gridSize: { xs: 6, xsm: 4, sm: 3, md: 2, lg: 2 }
  },
  jacket: {
    gridSize: { xs: 6, xsm: 4, sm: 3, md: 2, lg: 2 }
  },
  frame: {
    gridSize: { xs: 12, xsm: 6, sm: 4, md: 3, lg: 3 }
  },
  partner: {
    gridSize: { xs: 12, xsm: 6, sm: 4, md: 3, lg: 3 }
  },
  nameplate: {
    gridSize: { xs: 12, xsm: 12, sm: 6, md: 6, lg: 4 }
  }
}

const Home = () => {
  const PAGE_SIZE = 60
  const location = useLocation()
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState([])
  const [error, setError] = useState(null)
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)

  const params = useMemo(() => {
    return new URLSearchParams(location.search)
  }, [location.search])

  const categoryFromPath = location.pathname.split("/")[2]
  const categoryKey = resolveCategoryKey(categoryFromPath)
  const searchKeyword = params.get("search") || ""
  const categoryLabel = getCategoryLabel(categoryKey)
  const categoryLayout = CATEGORY_LAYOUT[categoryKey] || CATEGORY_LAYOUT.chara

  useEffect(() => {
    let ignore = false

    const loadData = async () => {
      setLoading(true)
      setError(null)

      try {
        const items = await getCollectionsByCategory(categoryKey, searchKeyword)
        if (!ignore) {
          setData(items)
        }
      } catch (err) {
        if (!ignore) {
          setError(err)
          setData([])
        }
      } finally {
        if (!ignore) {
          setLoading(false)
        }
      }
    }

    loadData()
    window.scrollTo(0, 0)

    return () => {
      ignore = true
    }
  }, [categoryKey, searchKeyword])

  useEffect(() => {
    setVisibleCount(PAGE_SIZE)
  }, [data, PAGE_SIZE])

  const displayedData = useMemo(() => {
    return data.slice(0, visibleCount)
  }, [data, visibleCount])

  const fetchMoreData = () => {
    setVisibleCount((prevCount) => {
      return Math.min(prevCount + PAGE_SIZE, data.length)
    })
  }

  const allLoaded = displayedData.length >= data.length

  return (
    <>
      <div className="space-top-80"></div>
      <GridContainer>
        <AnimeFilters />
      </GridContainer>
      {loading && (
        <>
          <Loading />
          <div className="space-top-60"></div>
          <LoadingCardSkeleton />
        </>
      )}
      {!loading && !error && (
        <>
          <div className={styles.titleContainer}>
            <h4 className={styles.title}>{categoryLabel}</h4>
          </div>
          {data.length > 0 && (
            <InfiniteCardList
              data={displayedData}
              allLoaded={allLoaded}
              cardType={CARD_TYPES.DEFAULT}
              fetchMoreData={fetchMoreData}
              cardGridSizeOverride={categoryLayout.gridSize}
            />
          )}
        </>
      )}
      {!loading && !error && data.length === 0 && (
        <ResultNotFound message="No Results" />
      )}
      {error && <ResultNotFound message={error.message} />}
    </>
  )
}
export default Home
